import { useDefaultPaneSize, usePanePointer } from "./editor/react.tsx";
import { useEffect, useId, useRef, useState } from "react";
import {
  CaretDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CaretUpIcon
} from "@phosphor-icons/react";
import {
  clampSplitPaneRatio,
  splitPaneBounds,
  splitPaneKeyboardRatio
} from "./split-pane-model";

interface SplitPaneProps {
  /** vertical = panes side by side (col-resize), horizontal = stacked (row-resize) */
  direction: "vertical" | "horizontal";
  /** Controlled percentage occupied by the first pane. */
  ratio?: number;
  /** Initial percentage for an uncontrolled split. */
  defaultRatio?: number;
  /** Initial CSS-pixel size for the first pane. Overrides defaultRatio once measured. */
  defaultFirstPaneSize?: number;
  /** Reports pointer and keyboard ratio changes. */
  onRatioChange?: (ratio: number) => void;
  min?: number;
  max?: number;
  /** Collapse the second pane (and divider); the first pane takes 100%.
   * Both panes stay mounted so iframes/editors keep their state. */
  secondHidden?: boolean;
  /** Which pane the divider's fold button collapses (divider stays visible
   * with a restore chevron; drag is disabled while folded). */
  collapsible?: "first" | "second";
  /** Accessible name for the first-pane splitter. */
  separatorLabel?: string;
  /** Accessible name for the pane controlled by the collapse button. */
  collapsiblePanelLabel?: string;
  children: [React.ReactNode, React.ReactNode];
}

export function SplitPane({
  direction,
  ratio: controlledRatio,
  defaultRatio = 50,
  defaultFirstPaneSize,
  onRatioChange,
  min = 15,
  max = 85,
  secondHidden = false,
  collapsible,
  separatorLabel = "First pane",
  collapsiblePanelLabel,
  children
}: SplitPaneProps) {
  const bounds = splitPaneBounds(min, max, defaultRatio);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [uncontrolledRatio, setUncontrolledRatio] = useState(bounds.defaultRatio);
  const [dragging, setDragging] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const pointer = usePanePointer(setDragging);
  const isVertical = direction === "vertical";
  const firstPaneId = useId();
  const secondPaneId = useId();
  const ratio = controlledRatio ?? uncontrolledRatio;
  const currentRatio = clampSplitPaneRatio(ratio, bounds);
  const accessibleRatio = Number(currentRatio.toFixed(2));
  const isCollapsed = collapsible !== undefined && collapsed;
  const accessibleSeparatorLabel = separatorLabel.trim() || "First pane";
  const collapsiblePanelName =
    collapsiblePanelLabel?.trim() ||
    `${collapsible === "first" ? "first" : "second"} pane`;

  useDefaultPaneSize(containerRef, controlledRatio, defaultFirstPaneSize, isVertical, min, max, setUncontrolledRatio);

  function updateRatio(next: number): void {
    const clamped = clampSplitPaneRatio(next, bounds);
    if (controlledRatio === undefined) setUncontrolledRatio(clamped);
    if (clamped !== currentRatio) onRatioChange?.(clamped);
  }

  const hideFirst = collapsible === "first" && isCollapsed;
  const hideSecond = secondHidden || (collapsible === "second" && isCollapsed);

  function onPointerMove(event: React.PointerEvent<HTMLDivElement>) {
    pointer({ _tag: "Move", target: event.currentTarget, container: containerRef.current,
      input: { pointerId: event.pointerId, collapsed: isCollapsed, vertical: isVertical, client: isVertical ? event.clientX : event.clientY, min, max },
      notify: updateRatio });
  }

  function finishPointerResize(event: React.PointerEvent<HTMLDivElement>) {
    pointer({ _tag: "End", target: event.currentTarget, pointerId: event.pointerId });
  }

  function stopPointerResize() { pointer({ _tag: "Stop" }); }

  useEffect(() => {
    if (isCollapsed || secondHidden) stopPointerResize();
  }, [isCollapsed, secondHidden, pointer]);

  // Panes get pointer-events disabled while dragging so iframes and the
  // editor cannot swallow the pointer; capture keeps events on the divider.
  const paneClass = dragging ? "pointer-events-none select-none" : "";

  const foldIcon = (() => {
    if (!collapsible) return null;
    if (isVertical) {
      const foldedLeft = collapsible === "first" ? collapsed : !collapsed;
      return foldedLeft ? (
        <CaretRightIcon size={11} weight="bold" />
      ) : (
        <CaretLeftIcon size={11} weight="bold" />
      );
    }
    const foldedDown = collapsible === "second" ? collapsed : !collapsed;
    return foldedDown ? (
      <CaretUpIcon size={11} weight="bold" />
    ) : (
      <CaretDownIcon size={11} weight="bold" />
    );
  })();

  return (
    <div
      ref={containerRef}
      className={`flex h-full min-h-0 w-full min-w-0 ${isVertical ? "flex-row" : "flex-col"}`}
    >
      <div
        id={firstPaneId}
        hidden={hideFirst}
        style={{
          [isVertical ? "width" : "height"]:
            hideSecond || hideFirst ? "100%" : `${currentRatio}%`
        }}
        className={`shrink-0 overflow-hidden ${isVertical ? "h-full" : "w-full"} ${paneClass}`}
      >
        {children[0]}
      </div>
      <div
        hidden={secondHidden}
        className={`group relative z-10 flex shrink-0 items-center justify-center ${
          isVertical
            ? `-mx-[5px] w-[11px] flex-row ${isCollapsed ? "" : "cursor-col-resize"}`
            : `-my-[5px] h-[11px] flex-col ${isCollapsed ? "" : "cursor-row-resize"}`
        }`}
      >
        <div
          role="separator"
          tabIndex={isCollapsed ? -1 : 0}
          aria-label={accessibleSeparatorLabel}
          aria-orientation={isVertical ? "vertical" : "horizontal"}
          aria-valuemin={bounds.min}
          aria-valuemax={bounds.max}
          aria-valuenow={accessibleRatio}
          aria-valuetext={
            isCollapsed
              ? `Collapsed; restores to ${accessibleRatio}% for the first pane`
              : `${accessibleRatio}% for the first pane`
          }
          aria-controls={firstPaneId}
          aria-disabled={isCollapsed || undefined}
          onKeyDown={(event) => {
            if (isCollapsed) return;
            const next = splitPaneKeyboardRatio({
              ratio: currentRatio,
              key: event.key,
              direction,
              shiftKey: event.shiftKey,
              bounds
            });
            if (next === null) return;
            event.preventDefault();
            updateRatio(next);
          }}
          onPointerDown={(event) => {
            pointer({ _tag: "Start", target: event.currentTarget,
              input: { pointerId: event.pointerId, button: event.button, primary: event.isPrimary, collapsed: isCollapsed },
              prevent: () => event.preventDefault() });
          }}
          onPointerMove={onPointerMove}
          onPointerUp={finishPointerResize}
          onPointerCancel={finishPointerResize}
          onLostPointerCapture={(event) => {
            pointer({ _tag: "Lost", target: event.currentTarget, pointerId: event.pointerId });
          }}
          className="absolute inset-0 z-0 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-kumo-brand"
        />
        <div
          className={`pointer-events-none absolute ${isVertical ? "inset-y-0 left-1/2 w-px -translate-x-1/2" : "inset-x-0 top-1/2 h-px -translate-y-1/2"} ${
            dragging ? "bg-kumo-brand" : "bg-kumo-line"
          }`}
        />
        {collapsible ? (
          <button
            type="button"
            aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${collapsiblePanelName}`}
            aria-controls={collapsible === "first" ? firstPaneId : secondPaneId}
            aria-expanded={!isCollapsed}
            title={`${isCollapsed ? "Expand" : "Collapse"} ${collapsiblePanelName}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => {
              stopPointerResize();
              setCollapsed((value) => !value);
            }}
            className={`relative z-10 flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full border border-kumo-line bg-kumo-base text-kumo-subtle shadow-sm transition-opacity duration-200 ease-in-out hover:text-kumo-default group-hover:opacity-100 focus-visible:opacity-100 ${
              isCollapsed ? "opacity-50" : "opacity-0"
            }`}
          >
            {foldIcon}
          </button>
        ) : null}
      </div>
      <div
        id={secondPaneId}
        hidden={hideSecond}
        className={`min-h-0 min-w-0 flex-1 overflow-hidden ${paneClass}`}
      >
        {children[1]}
      </div>
    </div>
  );
}
